import React from 'react';
import { View, Modal, Pressable, ActivityIndicator, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/theme';
import { SubHeading, Body, Caption } from '@/components/ui/Text';
import type { InboxProviderId } from '@/services/inboxSync';

type EmailProviderSelectionModalProps = {
  visible: boolean;
  onClose: () => void;
  /** iOS: called after the native sheet finishes dismissing (use to avoid stacking presenters). */
  onNativeDismiss?: () => void;
  connectingProvider: InboxProviderId | null;
  disconnectingProvider?: InboxProviderId | null;
  providerConnections?: Partial<Record<InboxProviderId, boolean>>;
  onConnectProvider: (provider: InboxProviderId) => void;
  onDisconnectProvider?: (provider: InboxProviderId) => void;
};

export function EmailProviderSelectionModal({
  visible,
  onClose,
  onNativeDismiss,
  connectingProvider,
  disconnectingProvider,
  providerConnections,
  onConnectProvider,
  onDisconnectProvider,
}: EmailProviderSelectionModalProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
      presentationStyle="pageSheet"
      onDismiss={onNativeDismiss}
    >
      <View style={{ flex: 1, backgroundColor: theme.colors.surface }}>
        <View
          style={{
            flex: 1,
            paddingHorizontal: theme.spacing(3),
            paddingBottom: theme.spacing(3),
            paddingTop: Platform.OS === 'android' ? insets.top + theme.spacing(2) : theme.spacing(3),
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: theme.spacing(2),
            }}
          >
            <SubHeading>Connect Your Email</SubHeading>
            <Pressable onPress={onClose}>
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </Pressable>
          </View>

          <Body style={{ color: theme.colors.textSecondary, marginBottom: theme.spacing(3) }}>
            Connect your email to automatically detect and import trips from your booking confirmations.
          </Body>

          <View style={{ gap: theme.spacing(1.5) }}>
            {(['gmail', 'outlook', 'icloud'] as InboxProviderId[]).map((provider) => {
              const isConnecting = connectingProvider === provider;
              const isDisconnecting = disconnectingProvider === provider;
              const isConnected = Boolean(providerConnections?.[provider]);
              const providerLabels: Record<InboxProviderId, string> = {
                gmail: 'Gmail',
                outlook: 'Outlook',
                icloud: 'iCloud',
              };
              const providerIcons: Record<InboxProviderId, keyof typeof Ionicons.glyphMap> = {
                gmail: 'mail',
                outlook: 'mail-outline',
                icloud: 'cloud',
              };

              return (
                <Pressable
                  key={provider}
                  onPress={() => onConnectProvider(provider)}
                  disabled={isConnecting || isDisconnecting}
                  style={({ pressed }) => ({
                    padding: theme.spacing(2),
                    borderRadius: theme.radii.lg,
                    borderWidth: 1,
                    borderColor: theme.colors.borderMuted,
                    backgroundColor: pressed ? theme.colors.background : theme.colors.surface,
                    opacity: (isConnecting || isDisconnecting) ? 0.6 : 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: theme.spacing(2),
                  })}
                >
                  {isConnecting || isDisconnecting ? (
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                  ) : (
                    <Ionicons name={providerIcons[provider]} size={24} color={theme.colors.primary} />
                  )}
                  <View style={{ flex: 1 }}>
                    <Body style={{ fontWeight: '600' }}>{providerLabels[provider]}</Body>
                    {isConnected && (
                      <Caption style={{ color: theme.colors.success || theme.colors.primary, marginTop: 2 }}>
                        Connected
                      </Caption>
                    )}
                    {provider === 'icloud' && (
                      <Caption style={{ color: theme.colors.textSecondary, marginTop: 2 }}>
                        Requires IMAP settings
                      </Caption>
                    )}
                  </View>
                  {isConnected && onDisconnectProvider ? (
                    <Pressable
                      onPress={() => onDisconnectProvider(provider)}
                      disabled={isDisconnecting || isConnecting}
                      style={({ pressed }) => ({
                        paddingHorizontal: theme.spacing(1.5),
                        paddingVertical: theme.spacing(0.75),
                        borderRadius: theme.radii.md,
                        borderWidth: 1,
                        borderColor: theme.colors.borderMuted,
                        opacity: pressed ? 0.7 : 1,
                      })}
                    >
                      <Caption style={{ color: theme.colors.error }}>
                        {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                      </Caption>
                    </Pressable>
                  ) : (
                    <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />
                  )}
                </Pressable>
              );
            })}
          </View>
        </View>
      </View>
    </Modal>
  );
}


